import { useMutation } from "@tanstack/react-query";
import {
  bulkCreateDictionaryConnections,
  CreateDictionaryConnectionParams
} from "PFCore/services/custom_values";
import { MutationOptions } from "PFTypes/request_options";

import { ActivitiesCollection } from "../activities/query_keys";
import { useActivitiesInvalidate } from "../activities/use_activities_invalidate";
import { useCustomValuesInvalidate } from "../custom_fields/admin";
import { useCustomValueInvalidate } from "../custom_fields/use_custom_value_invalidate";
import { useCurrentProfile } from "../profile";
import { useProfileInvalidate } from "../profile/use_profile_invalidate";
import { useSkillsFrameworksInvalidate } from "../skills_frameworks/use_skills_frameworks_invalidate";

export const useBulkDictionaryConnectionCreate = ({
  onSuccess,
  ...options
}: MutationOptions<CreateDictionaryConnectionParams[], PromiseSettledResult<any>[]> = {}) => {
  const { data: currentProfile } = useCurrentProfile();
  const { invalidateCurrentProfile } = useProfileInvalidate();
  const { invalidateByCollection } = useActivitiesInvalidate();
  const { invalidate: invalidateCustomValues } = useCustomValuesInvalidate();
  const { invalidateCustomValueProfileScope } = useCustomValueInvalidate();
  const { invalidateProfileSkillsFrameworks } = useSkillsFrameworksInvalidate();

  return useMutation<PromiseSettledResult<any>[], unknown, CreateDictionaryConnectionParams[]>({
    mutationFn: (payloads: CreateDictionaryConnectionParams[]) => bulkCreateDictionaryConnections(payloads),
    onSuccess: (...params) => {
      const payloads = params[1];
      invalidateCurrentProfile();
      invalidateByCollection(ActivitiesCollection.MatchingActivityStatus);
      invalidateCustomValues();
      payloads.forEach(({ customValueId }) =>
        invalidateCustomValueProfileScope(customValueId, currentProfile.id)
      );
      invalidateProfileSkillsFrameworks(currentProfile.id);
      onSuccess?.(...params);
    },
    ...options
  });
};
